import { Avatar, AvatarGroup, Badge, Box, Button, Checkbox, Flex, Grid, Heading, HStack, Icon, Input, Spinner, Text, VStack, keyframes, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiCheck, FiCheckCircle, FiList, FiLock, FiMail, FiMessageSquare, FiShield, FiUser, FiUserPlus, FiUsers } from "react-icons/fi";
import { getGroupSignupMeta, submitGroupSignup, getProfile, isLoggedIn, GroupSignupMeta, EMAIL_REGEX } from "@lubbezposrednio-frontend/core";
import { OrganizerShell } from "../OrganizerDashboardView/components/OrganizerShell";

// Klucze: po rejestracji/logowaniu z poziomu zaproszenia OrganizerShell automatycznie dołącza do grupy.
export const PENDING_GROUP_JOIN_KEY = "pendingGroupJoinToken";
export const PENDING_GROUP_JOIN_NAME_KEY = "pendingGroupJoinName";

const mediaUrl = (url?: string | null) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : undefined);

// Nasz animowany fioletowy gradient (jak na ekranach auth): pływające, rozmyte orby.
const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -22px) scale(1.08); }
`;
const Orb: React.FC<{ size: string; color: string; top?: string; bottom?: string; left?: string; right?: string; delay?: string }> = ({ size, color, top, bottom, left, right, delay = "0s" }) => (
  <Box position="absolute" top={top} bottom={bottom} left={left} right={right} w={size} h={size} bg={color} opacity={0.5} borderRadius="full" filter="blur(60px)" animation={`${float} 12s ease-in-out infinite`} sx={{ animationDelay: delay }} pointerEvents="none" />
);

// Treść nagłówka grupy (biała, na ciemnym gradiencie): awatar + nazwa + stack członków + wymagania dostępu.
const HeroInner: React.FC<{ meta: GroupSignupMeta }> = ({ meta }) => {
  const previews = meta.member_previews ?? [];
  const count = meta.members_count ?? previews.length;
  return (
    <VStack position="relative" spacing="14px" textAlign="center">
      <Avatar
        w={{ base: "78px", md: "92px" }}
        h={{ base: "78px", md: "92px" }}
        borderRadius="24px"
        name={meta.name}
        src={mediaUrl(meta.avatar_url)}
        bg="whiteAlpha.300"
        color="white"
        icon={<Icon as={FiUsers} boxSize="38px" />}
        border="3px solid rgba(255,255,255,0.5)"
        boxShadow="0 10px 26px rgba(0,0,0,0.25)"
      />
      <VStack spacing="4px">
        <Text fontSize="12px" fontWeight="700" textTransform="uppercase" letterSpacing="0.1em" color="whiteAlpha.800">
          Zaproszenie do grupy
        </Text>
        <Heading fontSize={{ base: "27px", md: "34px" }} color="white" fontWeight="900" lineHeight="1.1">
          {meta.title || meta.name}
        </Heading>
      </VStack>
      {meta.description && (
        <Text color="whiteAlpha.900" fontSize={{ base: "14px", md: "15.5px" }} maxW="560px">
          {meta.description}
        </Text>
      )}

      {(previews.length > 0 || count > 0) && (
        <HStack spacing="11px" bg="whiteAlpha.200" borderRadius="full" px="14px" py="7px" backdropFilter="blur(6px)">
          {previews.length > 0 && (
            <AvatarGroup size="sm" max={5} spacing="-10px">
              {previews.map((p, i) => (
                <Avatar key={i} name={p.initials} getInitials={s => s} src={mediaUrl(p.avatar_url)} bg="whiteAlpha.400" color="white" border="2px solid rgba(255,255,255,0.6)" />
              ))}
            </AvatarGroup>
          )}
          <Text fontSize="13px" fontWeight="700" color="white">{count} {count === 1 ? "członek" : "członków"}</Text>
        </HStack>
      )}

      <HStack spacing="8px" wrap="wrap" justify="center">
        {meta.organizer_name && (
          <HStack spacing="6px" bg="whiteAlpha.200" px="11px" py="5px" borderRadius="full">
            <Icon as={FiUser} boxSize="13px" color="white" />
            <Text fontSize="12.5px" fontWeight="600" color="white" noOfLines={1} maxW="220px">{meta.organizer_name}</Text>
          </HStack>
        )}
        {meta.verification_required ? (
          <HStack spacing="6px" bg="whiteAlpha.300" px="11px" py="5px" borderRadius="full">
            <Icon as={FiShield} boxSize="13px" color="white" />
            <Text fontSize="12.5px" fontWeight="700" color="white">Tylko zweryfikowani</Text>
          </HStack>
        ) : meta.account_required ? (
          <HStack spacing="6px" bg="whiteAlpha.300" px="11px" py="5px" borderRadius="full">
            <Icon as={FiLock} boxSize="13px" color="white" />
            <Text fontSize="12.5px" fontWeight="700" color="white">Tylko z kontem</Text>
          </HStack>
        ) : null}
      </HStack>
    </VStack>
  );
};

// Pełnoekranowe tło z animowanym fioletowym gradientem + orby (dla niezalogowanych).
const GradientBg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box position="relative" minH="100vh" w="100%" overflow="hidden" bgGradient="linear(150deg, #25004f 0%, #430098 46%, #6a1fc2 100%)">
    <Orb size="360px" color="#7b2ff7" top="-90px" left="-80px" />
    <Orb size="300px" color="#e51f44" bottom="-100px" right="-70px" delay="3s" />
    <Orb size="240px" color="#6a1fc2" top="40%" right="12%" delay="6s" />
    <Box position="absolute" inset="0" bgGradient="radial(circle at 78% 10%, rgba(255,255,255,0.10), transparent 46%)" pointerEvents="none" />
    <Box position="relative">{children}</Box>
  </Box>
);

const WhiteCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <VStack w="100%" bg="white" borderRadius="24px" boxShadow="0 18px 50px rgba(0,0,0,0.18)" p={{ base: "32px 24px", md: "40px 36px" }} spacing="20px">
    {children}
  </VStack>
);

export const GroupSignupView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const loggedIn = isLoggedIn();

  const [meta, setMeta] = useState<GroupSignupMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loggedInEmail, setLoggedInEmail] = useState<string | null>(null);
  const [meName, setMeName] = useState("");
  const [meAvatar, setMeAvatar] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [devConfirm, setDevConfirm] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Zapisy do grupy";
    if (!token) return;
    getGroupSignupMeta(token)
      .then(r => setMeta(r.data))
      .catch(() => setMeta(null))
      .finally(() => setLoading(false));

    if (loggedIn) {
      getProfile()
        .then(r => {
          const u = r.data?.user;
          if (u?.email) {
            setLoggedInEmail(u.email);
            setEmail(u.email);
          }
          const fullName = u?.is_organization ? u?.organization_name || "" : `${u?.first_name || ""} ${u?.last_name || ""}`.trim();
          setMeName(fullName);
          setMeAvatar(u?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar_url}` : "");
        })
        .catch(() => undefined);
    }
  }, [token, loggedIn]);

  const emailValid = EMAIL_REGEX.test(email.trim());

  const doSubmit = async (consentValue: boolean) => {
    if (!token || !meta?.open || !emailValid) return;
    setSubmitting(true);
    try {
      const r = await submitGroupSignup(token, { email: email.trim(), name: name.trim() || undefined, consent: consentValue });
      setConfirmed(!!r.data?.confirmed);
      setDone(true);
      if (r.data?.confirmation_token) setDevConfirm(r.data.confirmation_token);
    } catch (e: any) {
      toast({ title: e?.response?.data?.errors?.[0] || "Nie udało się zapisać. Spróbuj ponownie.", status: "error", isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  // Ścieżka „konto": zapamiętaj token + nazwę grupy i wyślij do rejestracji/logowania — po zalogowaniu shell dołączy automatycznie.
  const goAuth = (path: "/rejestracja" | "/logowanie") => {
    if (token) {
      localStorage.setItem(PENDING_GROUP_JOIN_KEY, token);
      localStorage.setItem(PENDING_GROUP_JOIN_NAME_KEY, meta?.name || "");
    }
    navigate(path);
  };

  // Powłoka: zawsze animowany gradient na CAŁOŚĆ tła. Zalogowany dodatkowo ma sidebar (OrganizerShell).
  const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    loggedIn ? <OrganizerShell><GradientBg>{children}</GradientBg></OrganizerShell> : <GradientBg>{children}</GradientBg>;

  // Wyśrodkowany kontener treści na gradiencie.
  const Center: React.FC<{ children: React.ReactNode; maxW?: string }> = ({ children, maxW = "960px" }) => (
    <Flex minH={loggedIn ? "calc(100vh - 64px)" : "100vh"} w="100%" align="center" justify="center" px={{ base: "18px", md: "32px" }} py={{ base: "40px", md: "56px" }}>
      <VStack w="100%" maxW={maxW} spacing="26px">{children}</VStack>
    </Flex>
  );

  const benefit = (icon: React.ElementType, label: string) => (
    <HStack spacing="8px">
      <Icon as={icon} boxSize="15px" color="#1f9e18" />
      <Text fontSize="13px" color="gray.600">{label}</Text>
    </HStack>
  );

  if (loading) {
    return <Frame><Center maxW="560px"><Spinner color="white" size="lg" thickness="3px" /></Center></Frame>;
  }

  if (!meta || !meta.open) {
    return (
      <Frame>
        <Center maxW="520px">
          <WhiteCard>
            <Heading fontSize="24px" color="primary" textAlign="center">Zapisy są zamknięte</Heading>
            <Text color="gray.500" textAlign="center">Ten link do zapisów jest nieaktywny lub minął termin.</Text>
            <Button as={Link} to={loggedIn ? "/organizator/grupy" : "/"} variant="ghost" color="primary">
              {loggedIn ? "Twoje grupy" : "Strona główna"}
            </Button>
          </WhiteCard>
        </Center>
      </Frame>
    );
  }

  if (done) {
    return (
      <Frame>
        <Center maxW="520px">
          <WhiteCard>
            <Flex w="72px" h="72px" align="center" justify="center" borderRadius="full" bg="rgba(47,206,37,0.14)">
              <Icon as={confirmed ? FiCheckCircle : FiMail} boxSize="34px" color="#1f9e18" />
            </Flex>
            <Heading fontSize="24px" color="primary" textAlign="center">
              {confirmed ? "Dołączono do grupy!" : "Sprawdź swoją skrzynkę"}
            </Heading>
            <Text color="gray.500" textAlign="center">
              {confirmed
                ? `Jesteś członkiem grupy „${meta.name}". Będziesz dostawać zaproszenia do jej głosowań.`
                : `Wysłaliśmy e-mail z linkiem potwierdzającym. Kliknij go, aby dokończyć zapis do „${meta.name}".`}
            </Text>
            {confirmed && (
              <Button as={Link} to="/organizator/grupy?w=naleze" bg="accent" color="white" borderRadius="12px" _hover={{ bg: "#c41a3a" }}>
                Zobacz moje grupy
              </Button>
            )}
            {devConfirm && (
              <Button as={Link} to={`/zapisy/potwierdzenie?token=${devConfirm}`} variant="ghost" color="primary">
                [DEV] Potwierdź teraz
              </Button>
            )}
          </WhiteCard>
        </Center>
      </Frame>
    );
  }

  // ── Zalogowany: jeden klik. Rozgałęzienie SYNCHRONICZNE (isLoggedIn) — bez migania widoku. ──
  if (loggedIn) {
    // Dynamiczne info: już jesteś członkiem tej grupy.
    if (meta.already_member) {
      return (
        <Frame>
          <Center maxW="560px">
            <HeroInner meta={meta} />
            <WhiteCard>
              <Flex w="60px" h="60px" align="center" justify="center" borderRadius="full" bg="rgba(47,206,37,0.14)">
                <Icon as={FiCheckCircle} boxSize="30px" color="#1f9e18" />
              </Flex>
              <Heading fontSize="22px" color="primary" textAlign="center">Już należysz do tej grupy</Heading>
              <Text color="gray.500" textAlign="center">Masz pełny dostęp do jej głosowań, dyskusji i tematów.</Text>
              <Button as={Link} to={meta.group_id ? `/organizator/grupy/${meta.group_id}` : "/organizator/grupy?w=naleze"} w="100%" h="auto" py="13px" bg="accent" color="white" fontWeight="700" borderRadius="13px" _hover={{ bg: "#c41a3a" }}>
                Otwórz grupę
              </Button>
            </WhiteCard>
          </Center>
        </Frame>
      );
    }
    return (
      <Frame>
        <Center maxW="560px">
          <HeroInner meta={meta} />
          <WhiteCard>
            <VStack align="stretch" spacing="11px" w="100%">
              {benefit(FiCheckCircle, "Udział we wszystkich głosowaniach grupy")}
              {benefit(FiMessageSquare, "Dyskusje i komentarze na ścianie grupy")}
              {benefit(FiList, "Tematy (forum) grupy")}
            </VStack>

            <HStack spacing="12px" w="100%" bg="rgba(67,0,152,0.05)" border="1px solid rgba(67,0,152,0.08)" borderRadius="14px" p="12px 14px">
              <Avatar size="md" name={meName || loggedInEmail || "?"} src={meAvatar || undefined} bg="primary" color="white" flexShrink={0} />
              <Box minW={0}>
                <Text fontSize="12px" color="gray.500">Dołączasz jako</Text>
                <Text fontSize="15px" fontWeight="800" color="primary" noOfLines={1}>{meName || "Twoje konto"}</Text>
                <Text fontSize="12.5px" color="gray.500" noOfLines={1}>{loggedInEmail || "Wczytywanie…"}</Text>
              </Box>
            </HStack>

            <Button w="100%" h="auto" py="14px" bg="accent" color="white" fontSize="16px" fontWeight="700" borderRadius="13px" leftIcon={<FiCheckCircle />} isLoading={submitting} isDisabled={!loggedInEmail} onClick={() => doSubmit(true)} _hover={{ bg: "#c41a3a" }}>
              Dołącz jednym kliknięciem
            </Button>
          </WhiteCard>
        </Center>
      </Frame>
    );
  }

  // ── Niezalogowany: kompleksowy ekran z dwiema ścieżkami ──
  // Gdy grupa wymaga konta/weryfikacji — ścieżka „tylko e-mail" jest niedostępna.
  const emailPathBlocked = !!meta.account_required || !!meta.verification_required;

  return (
    <Frame>
      <Center maxW={emailPathBlocked ? "560px" : "940px"}>
        <HeroInner meta={meta} />

        {/* Ścieżki dołączenia */}
        <Grid templateColumns={{ base: "1fr", md: emailPathBlocked ? "1fr" : "1fr 1fr" }} gap="20px" w="100%" alignItems="stretch">
            {/* A — tylko głosowania (e-mail) — niedostępne gdy grupa wymaga konta/weryfikacji */}
            {!emailPathBlocked && (
            <VStack align="stretch" spacing="16px" bg="white" borderRadius="22px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 10px 32px rgba(67,0,152,0.07)" p={{ base: "24px 20px", md: "30px 28px" }}>
              <HStack justify="space-between">
                <Flex w="46px" h="46px" align="center" justify="center" borderRadius="13px" bg="rgba(67,0,152,0.07)">
                  <Icon as={FiMail} boxSize="22px" color="primary" />
                </Flex>
                <Badge bg="rgba(67,0,152,0.06)" color="primary" borderRadius="full" px="10px" py="3px" fontSize="11px" textTransform="none" fontWeight="700">Bez konta</Badge>
              </HStack>
              <Box>
                <Heading fontSize="19px" color="primary" fontWeight="800" mb="4px">Tylko głosowania</Heading>
                <Text fontSize="13.5px" color="gray.500">Podaj e-mail — będziesz dostawać zaproszenia do głosowań tej grupy i w nich głosować.</Text>
              </Box>

              <VStack align="stretch" spacing="11px">
                <Box>
                  <Text fontSize="12.5px" fontWeight="700" color="primary" mb="5px">Adres e-mail</Text>
                  <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="twoj@email.pl" borderRadius="12px" />
                </Box>
                <Box>
                  <Text fontSize="12.5px" fontWeight="700" color="primary" mb="5px">Imię i nazwisko (opcjonalnie)</Text>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jan Kowalski" borderRadius="12px" />
                </Box>
                <Checkbox isChecked={consent} onChange={e => setConsent(e.target.checked)} colorScheme="purple" alignItems="flex-start">
                  <Text fontSize="12.5px" color="gray.600">
                    Wyrażam zgodę na przetwarzanie mojego adresu e-mail w celu zapisu do grupy i otrzymywania zaproszeń do głosowań.
                  </Text>
                </Checkbox>
              </VStack>

              <Button mt="auto" w="100%" h="auto" py="13px" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" fontSize="15px" fontWeight="700" borderRadius="13px" leftIcon={<FiCheck />} isDisabled={!(emailValid && consent)} isLoading={submitting} onClick={() => doSubmit(consent)} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
                Zapisz się e-mailem
              </Button>
            </VStack>
            )}

            {/* B — pełne członkostwo (konto) */}
            <VStack align="stretch" spacing="16px" bg="white" borderRadius="22px" border="2px solid" borderColor="accent" boxShadow="0 16px 40px rgba(229,31,68,0.12)" p={{ base: "24px 20px", md: "30px 28px" }} position="relative">
              <HStack justify="space-between">
                <Flex w="46px" h="46px" align="center" justify="center" borderRadius="13px" bgGradient="linear(135deg, #430098, #7b2ff7)">
                  <Icon as={FiUserPlus} boxSize="22px" color="white" />
                </Flex>
                <Badge bg="accent" color="white" borderRadius="full" px="10px" py="3px" fontSize="11px" textTransform="none" fontWeight="700">Polecane</Badge>
              </HStack>
              <Box>
                <Heading fontSize="19px" color="primary" fontWeight="800" mb="4px">Pełne członkostwo</Heading>
                <Text fontSize="13.5px" color="gray.500">Załóż konto, a dołączymy Cię do grupy automatycznie. Zyskujesz pełny udział w jej życiu.</Text>
              </Box>

              <VStack align="stretch" spacing="9px" bg="#FAF8FE" borderRadius="13px" p="14px">
                {benefit(FiCheckCircle, "Udział we wszystkich głosowaniach grupy")}
                {benefit(FiMessageSquare, "Dyskusje i komentarze na ścianie grupy")}
                {benefit(FiList, "Tematy (forum) grupy")}
              </VStack>

              {meta.verification_required && (
                <HStack spacing="8px" bg="rgba(29,155,240,0.08)" borderRadius="11px" p="10px 12px" align="flex-start">
                  <Icon as={FiShield} boxSize="15px" color="#1d9bf0" mt="2px" />
                  <Text fontSize="12px" color="#1577c2">Ta grupa wymaga zweryfikowanego konta — po rejestracji zweryfikuj tożsamość, aby dokończyć dołączenie.</Text>
                </HStack>
              )}

              <VStack align="stretch" spacing="9px" mt="auto">
                <Button w="100%" h="auto" py="13px" bg="accent" color="white" fontSize="15px" fontWeight="700" borderRadius="13px" leftIcon={<FiUserPlus />} onClick={() => goAuth("/rejestracja")} _hover={{ bg: "#c41a3a" }}>
                  Załóż konto i dołącz
                </Button>
                <Text fontSize="12.5px" color="gray.500" textAlign="center">
                  Masz już konto?{" "}
                  <Text as="button" type="button" display="inline" color="accent" fontWeight="700" onClick={() => goAuth("/logowanie")} _hover={{ textDecoration: "underline" }}>
                    Zaloguj się
                  </Text>
                </Text>
              </VStack>
            </VStack>
          </Grid>
      </Center>
    </Frame>
  );
};
